// Latin -> native-script TRANSLITERATION for menu names. Pure functions only: no React, no
// network, no module state.
//
// Mirrors admin-mobile-app/src/utils/transliterate.js. Keep the two byte-identical apart from this one header line, following the existing money.js / menuName.js / addableOrder.js / modifierSelection.js convention.
//
// ============================================================================
// WHAT THIS IS, AND WHAT IT IS NOT
// ============================================================================
//
// TRANSLITERATION IS NOT TRANSLATION. This converts the SOUND of a Latin-script name into
// another script. It does not know what a word means:
//
//     "Papadum"  -> பபடும்    (phonetic)      ... but Tamil speakers say அப்பளம்
//     "Dosa"     -> டோசா      (correct)
//     "Naan"     -> நான்      (correct)
//     "Breads"   -> ப்ரெட்ஸ்   (phonetic)      ... the Tamil word is ரொட்டி
//
// So the output is a DRAFT for a human to correct, never a finished name. Loanwords and
// proper nouns come out well; anything with a real word in the target language comes out as
// a phonetic imitation of the English. The UI that calls this MUST show the result for review
// before saving, and must never overwrite a name a human already entered.
//
// This is why the feature is "fill in the blanks for review", not "auto-translate the menu".
// Machine TRANSLATION remains out of scope; a wrong machine translation on a kitchen chit is
// worse than a blank, because nobody can tell it is wrong by looking at it.
//
// ============================================================================
// WHY A RULE TABLE IN THE REPO RATHER THAN AN API
// ============================================================================
//
// Considered and rejected: Google's transliteration endpoint (`inputtools.google.com`), which
// several libraries still wrap. It is DEPRECATED, undocumented, and no longer serves over
// SSL -- unusable from an HTTPS admin page, and not something to hang a menu feature on.
// Cloud translation APIs cost money and need a key.
//
// A rule table costs nothing, needs no key, works offline in the mobile app, and is
// deterministic -- which matters more than it sounds, because deterministic output can be
// unit-tested, and this text ends up on a printed menu. The trade is accuracy: a rule table
// is a rough phonetic mapping, which is exactly why the output is framed as a draft.
//
// If a better engine is ever wanted, `transliterate(text, lang)` is the seam to swap. Callers
// depend only on that signature.
//
// ============================================================================
// ADDING A LANGUAGE
// ============================================================================
//
// Add an entry to SCRIPTS keyed by the language code, with `vowels`, `consonants`, `virama`
// and `defaultVowel`. The engine below is script-agnostic: it is the standard abugida shape
// shared by Tamil, Devanagari, Telugu, Kannada, Malayalam, Bengali and Gujarati, so those are
// all table-only additions. A non-abugida script (Arabic, Thai, Han) would need a different
// engine, and `supportsTransliteration` returning false is the honest answer until then.

/** Longest-first so digraphs win: 'sh' must be tried before 's'. */
function byLengthDesc(keys) {
  return keys.slice().sort((a, b) => b.length - a.length);
}

// ---------------------------------------------------------------------------
// Tamil
// ---------------------------------------------------------------------------
//
// Tamil has ONE letter per place of articulation -- no separate voiced or aspirated forms --
// so k/g/kh/gh all collapse to க and p/b/ph/bh all collapse to ப. That is not an
// approximation on our part; it is how the script works, and it is why Tamil transliteration
// of English is more forgiving than, say, Devanagari would be.
//
// Grantha letters (ஜ ஷ ஸ ஹ) are used for sounds Tamil lacks. They are standard in modern
// usage for loanwords, which is most of what a menu contains.
const TAMIL = {
  label: 'Tamil',
  virama: '\u0BCD', // ் pulli, suppresses the inherent 'a'
  defaultVowel: 'a',
  vowels: {
    // latin: [independent, sign]
    a: ['\u0B85', ''],            // அ  (inherent in the bare consonant)
    aa: ['\u0B86', '\u0BBE'],     // ஆ  ா
    i: ['\u0B87', '\u0BBF'],      // இ  ி
    ee: ['\u0B88', '\u0BC0'],     // ஈ  ீ
    ii: ['\u0B88', '\u0BC0'],
    u: ['\u0B89', '\u0BC1'],      // உ  ு
    oo: ['\u0B8A', '\u0BC2'],     // ஊ  ூ
    uu: ['\u0B8A', '\u0BC2'],
    e: ['\u0B8E', '\u0BC6'],      // எ  ெ
    ea: ['\u0B88', '\u0BC0'],     // ஈ  ீ  "Tea", "Breads" -- without this 'ea' splits into
                                  //        e + a and emits a stray அ mid-word.
    ae: ['\u0B8F', '\u0BC7'],     // ஏ  ே
    ai: ['\u0B90', '\u0BC8'],     // ஐ  ை
    o: ['\u0B92', '\u0BCA'],      // ஒ  ொ
    oa: ['\u0B93', '\u0BCB'],     // ஓ  ோ
    au: ['\u0B94', '\u0BCC'],     // ஔ ௌ
    ou: ['\u0B94', '\u0BCC'],
  },
  consonants: {
    // Digraphs first (the sorter handles ordering, this is just for reading).
    zh: '\u0BB4',                 // ழ  the retroflex approximant, as in "Kozhi"
    sh: '\u0BB7',                 // ஷ
    ch: '\u0B9A',                 // ச
    th: '\u0BA4',                 // த  dental
    dh: '\u0BA4',                 // த
    ph: '\u0BAA',                 // ப
    bh: '\u0BAA',                 // ப
    gh: '\u0B95',                 // க
    kh: '\u0B95',                 // க
    jh: '\u0B9C',                 // ஜ
    // ங்க, not a bare ங. In Tamil ங effectively only ever appears before க, so "Pongal"
    // is பொங்கல் and "Mango" is மங்கோ. Emitting ங alone produced பொஙல், which is not
    // a word anyone would recognise.
    ng: '\u0B99\u0BCD\u0B95',     // ங்க
    ny: '\u0B9E',                 // ஞ
    // Singles.
    k: '\u0B95',                  // க
    g: '\u0B95',                  // க  (Tamil does not distinguish k/g)
    q: '\u0B95',
    c: '\u0B95',                  // க  hard c; 'ch' above catches the soft case
    j: '\u0B9C',                  // ஜ
    z: '\u0B9C',                  // ஜ
    t: '\u0B9F',                  // ட  retroflex; 'th' above gives the dental
    d: '\u0B9F',                  // ட
    n: '\u0BA8',                  // ந  (word-final becomes ன, handled below)
    p: '\u0BAA',                  // ப
    b: '\u0BAA',                  // ப
    f: '\u0BAA',                  // ப
    m: '\u0BAE',                  // ம
    y: '\u0BAF',                  // ய
    r: '\u0BB0',                  // ர
    l: '\u0BB2',                  // ல
    v: '\u0BB5',                  // வ
    w: '\u0BB5',                  // வ
    s: '\u0BB8',                  // ஸ
    h: '\u0BB9',                  // ஹ
    x: '\u0B95\u0BCD\u0BB8',      // க்ஸ
  },
  /**
   * Tamil uses ன for a word-final 'n' and ந initially. Purely cosmetic, but "நான்" versus
   * "நாந்" is the difference between a draft that reads naturally and one that reads wrong
   * to the person who has to approve it.
   */
  finalN: '\u0BA9',               // ன
};

const SCRIPTS = { ta: TAMIL };

/** Language codes this module can transliterate into. */
export const TRANSLITERATION_LANGUAGES = Object.keys(SCRIPTS);

/**
 * Can we produce a draft for this language?
 *
 * Callers use this to decide whether to offer the bulk action at all. Returning false for an
 * unsupported language is the honest answer: better no button than a button that emits
 * nonsense into a menu.
 */
export function supportsTransliteration(langCode) {
  const code = String(langCode || '').trim().toLowerCase();
  return Object.prototype.hasOwnProperty.call(SCRIPTS, code);
}

/** Human label for the target script, or the code itself. */
export function transliterationLabel(langCode) {
  const script = SCRIPTS[String(langCode || '').trim().toLowerCase()];
  return script ? script.label : String(langCode || '');
}

/**
 * Transliterate one whitespace-delimited word.
 *
 * The engine is the standard abugida walk:
 *   * a vowel at the START of a word becomes its independent letter (இ, அ, ...)
 *   * a vowel AFTER a consonant becomes a sign attached to it (ி, ா, ...)
 *   * a consonant with no vowel after it takes the virama (் in Tamil), which is what
 *     produces clusters like ட்ல in இட்லி and the final ம் in பபடும்
 *   * a bare consonant already carries the inherent 'a', so 'a' emits no sign
 */
function transliterateWord(word, script) {
  const vowelKeys = byLengthDesc(Object.keys(script.vowels));
  const consonantKeys = byLengthDesc(Object.keys(script.consonants));
  const lower = word.toLowerCase();

  let out = '';
  let i = 0;
  // Whether the previous emission was a consonant awaiting either a vowel sign or a virama.
  let pendingConsonant = false;

  const matchAt = (keys, pos) => {
    for (const key of keys) {
      if (lower.startsWith(key, pos)) return key;
    }
    return null;
  };

  while (i < lower.length) {
    let vowel = matchAt(vowelKeys, i);
    let consonant = matchAt(consonantKeys, i);
    const remaining = lower.length - i;

    // --- Two word-final adjustments, both about how English spells Indian names. ---

    // A word-final 'a' is nearly always LONG in a romanised Indian name: Dosa, Masala, Vada,
    // Tikka. Treating it as the inherent short 'a' emitted nothing at all, so "Dosa" came out
    // டொஸ -- visibly truncated, and the first thing a reviewer would flag.
    if (vowel === 'a' && remaining === 1 && pendingConsonant) vowel = 'aa';

    // A word-final 'y' is a vowel in English spelling, not a consonant: Curry, Fry, Crispy.
    // Read as the consonant ய it produced குர்ர்ய், a cluster with no vowel that cannot be
    // pronounced. Mapped to 'i' it gives குர்ரி, which reads.
    if (consonant === 'y' && remaining === 1 && pendingConsonant) {
      consonant = null;
      vowel = 'i';
    }

    // Prefer the LONGER match when both a vowel and a consonant could start here. Without
    // this, "ai" would be read as 'a' + 'i' and "sh" as 's' + 'h'.
    const takeVowel = vowel && (!consonant || vowel.length >= consonant.length);

    if (takeVowel) {
      const [independent, sign] = script.vowels[vowel];
      out += pendingConsonant ? sign : independent;
      pendingConsonant = false;
      i += vowel.length;
      continue;
    }

    if (consonant) {
      // Close off the previous consonant: no vowel followed it, so it is part of a cluster.
      if (pendingConsonant) out += script.virama;

      const isWordFinal = i + consonant.length >= lower.length;
      let glyph = script.consonants[consonant];
      if (script.finalN && consonant === 'n' && isWordFinal) glyph = script.finalN;

      out += glyph;
      pendingConsonant = true;
      i += consonant.length;
      continue;
    }

    // Anything the tables do not cover -- a digit, an ampersand, a hyphen -- passes through
    // untouched. A menu is full of "7Up", "A1" and "Chicken 65", and mangling those would be
    // worse than leaving them in Latin.
    if (pendingConsonant) {
      out += script.virama;
      pendingConsonant = false;
    }
    out += word[i];
    i += 1;
  }

  // A trailing consonant is closed with the virama: "Naan" ends ன், not னa.
  if (pendingConsonant) out += script.virama;
  return out;
}

/**
 * Transliterate a full name into `langCode`, or return '' if it cannot be done.
 *
 * Returns '' rather than throwing, and rather than echoing the input, for anything
 * unsupported or empty. A caller can then treat '' as "no draft available" uniformly, and an
 * empty draft can never be mistaken for a real translation.
 *
 * Word boundaries and interior punctuation are preserved, so "Chicken 65" and "Idli / Vada"
 * keep their shape.
 */
export function transliterate(text, langCode) {
  const script = SCRIPTS[String(langCode || '').trim().toLowerCase()];
  if (!script) return '';
  const source = String(text == null ? '' : text).trim();
  if (!source) return '';

  // Split on whitespace but KEEP it, so double spaces and tabs survive a round trip.
  return source
    .split(/(\s+)/)
    .map((chunk) => (/^\s+$/.test(chunk) || !chunk ? chunk : transliterateWord(chunk, script)))
    .join('');
}

/**
 * Read a row's existing translation map defensively. Mirrors `menuName.js`, which tolerates a
 * missing, null, malformed or array-shaped `ProductNameI18n` because most catalog rows have
 * never had one.
 */
export function existingTranslations(row) {
  const map = row && typeof row.ProductNameI18n === 'object' && row.ProductNameI18n !== null
    ? row.ProductNameI18n
    : null;
  return !map || Array.isArray(map) ? {} : map;
}

/** Does this row already have a usable name in `langCode`? Whitespace does not count. */
export function hasTranslation(row, langCode) {
  const code = String(langCode || '').trim().toLowerCase();
  const value = existingTranslations(row)[code];
  return typeof value === 'string' && value.trim() !== '';
}

/**
 * Build the draft list for a bulk fill: one entry per row that has NO name in `langCode`.
 *
 * ROWS THAT ALREADY HAVE A NAME ARE SKIPPED ENTIRELY, never re-generated. A human-entered
 * name always beats a machine draft, and silently replacing one would destroy work with no
 * way to get it back.
 *
 * Rows whose draft comes out empty (a name that is only digits or punctuation, so there is
 * nothing to sound out) are skipped too, since offering an empty suggestion is just a row the
 * user has to dismiss.
 *
 * Returns `[{ productId, englishName, draft }]` in input order.
 */
export function buildTransliterationDrafts(rows, langCode) {
  if (!Array.isArray(rows) || !supportsTransliteration(langCode)) return [];
  const out = [];
  for (const row of rows) {
    if (!row || typeof row !== 'object') continue;
    const productId = String(row.ProductID || '').trim();
    const englishName = String(row.ProductName || '').trim();
    if (!productId || !englishName) continue;
    if (hasTranslation(row, langCode)) continue;
    const draft = transliterate(englishName, langCode);
    if (!draft) continue;
    // A draft IDENTICAL to the English name is not a translation -- it happens for names made
    // only of digits or punctuation ("65", "A1"), where there is nothing to sound out. Saving
    // it would store the English string as a Tamil name; offering it is just a row the
    // reviewer has to dismiss. Skipped on both counts.
    if (draft === englishName) continue;
    out.push({ productId, englishName, draft });
  }
  return out;
}

/**
 * The `ProductNameI18n` map to PUT for one row: every EXISTING translation, plus the new one.
 *
 * THIS MERGE IS LOAD-BEARING, not defensive tidiness. The backend's update handler REPLACES
 * `ProductNameI18n` wholesale (`SET #ProductNameI18n = :ProductNameI18n`) and removes the
 * attribute entirely when it receives an empty map. So sending `{ ta: '...' }` for a row that
 * also had a Hindi name would silently delete the Hindi name. Harmless on a Tamil-only tenant
 * today, and a real data loss the moment a tenant enables a second non-default language --
 * which is exactly where this feature is heading.
 *
 * Blank values are dropped, matching the backend, so nothing here can create the empty-string
 * entries that `menuName.js` has to defend against.
 */
export function mergeTranslation(row, langCode, value) {
  const code = String(langCode || '').trim().toLowerCase();
  const next = {};
  const existing = existingTranslations(row);
  for (const key of Object.keys(existing)) {
    const v = existing[key];
    const k = String(key || '').trim().toLowerCase();
    if (k && typeof v === 'string' && v.trim()) next[k] = v.trim();
  }
  const incoming = String(value == null ? '' : value).trim();
  if (code && incoming) next[code] = incoming;
  else delete next[code];
  return next;
}
